import React from "react";
import { cn } from "@/lib/utils";

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  helperText?: string;
  error?: string | boolean;
  size?: "sm" | "md" | "lg";
  rightIcon?: React.ReactNode;
  selectClassName?: string;
  labelClassName?: string;
  helperClassName?: string;
  wrapperClassName?: string;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id,
      label,
      helperText,
      error = false,
      size = "md",
      rightIcon,
      className,
      selectClassName,
      labelClassName,
      helperClassName,
      wrapperClassName,
      children,
      ...props
    },
    ref
  ) => {
    const heightBySize: Record<NonNullable<SelectProps["size"]>, string> = {
      sm: "h-9",
      md: "h-11",
      lg: "h-12",
    };
    const basePaddingAndTextBySize: Record<NonNullable<SelectProps["size"]>, string> = {
      sm: "px-3 text-sm font-medium",
      md: "px-4 text-sm md:text-base font-medium",
      lg: "px-4 text-lg font-medium",
    };
    const rightIconPaddingBySize: Record<NonNullable<SelectProps["size"]>, string> = {
      sm: "pr-9",
      md: "pr-11",
      lg: "pr-12",
    };
    // Default dropdown chevron when rightIcon not provided; pass null to hide entirely
    const defaultChevron = (
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    const effectiveRightIcon = rightIcon === null ? null : (rightIcon ?? defaultChevron);
    const hasRightIcon = Boolean(effectiveRightIcon);

    const baseId = id ?? props.name;
    const describedById = (helperText || error) && baseId ? `${baseId}-desc` : undefined;

    return (
      <div className={cn("w-full", wrapperClassName)}>
        {label ? (
          <label htmlFor={baseId ?? undefined} className={cn("block mb-1.5 text-sm font-medium text-text-primary", labelClassName)}>
            {label}
          </label>
        ) : null}

        <div className={cn("relative", className)}>

          <select
            id={baseId ?? undefined}
            ref={ref}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedById}
            className={cn(
              "block w-full appearance-none rounded-md border border-gray-300 bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition",
              heightBySize[size],
              basePaddingAndTextBySize[size],
              hasRightIcon && rightIconPaddingBySize[size],
              Boolean(error) && "border-red-500 focus:border-red-500 focus:ring-red-500/20",
              selectClassName
            )}
            {...props}
          >
            {children}
          </select>

          {hasRightIcon ? (
            <span aria-hidden="true" className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 pointer-events-none")}>{effectiveRightIcon}</span>
          ) : null}
        </div>

        {helperText && !error ? (
          <p id={describedById} className={cn("mt-1 text-xs text-gray-500", helperClassName)}>
            {helperText}
          </p>
        ) : null}
        {error ? (
          <p id={describedById} className={cn("mt-1 text-xs text-primary", helperClassName)}>
            {typeof error === "string" ? error : "Invalid value"}
          </p>
        ) : null}
      </div>
    );
  }
);

Select.displayName = "Select";

export { Select };


